import React, { useState } from 'react';

const RetirementCorpus = ({ onBackToGoals, onGoalComplete }) => {
  // State for input values
  const [targetAmount, setTargetAmount] = useState('');
  const [sipDuration, setSipDuration] = useState('');
  const [returnRate, setReturnRate] = useState('');
  
  // State for calculation results
  const [results, setResults] = useState({
    requiredSip: 0,
    investedAmount: 0,
    estimatedReturns: 0,
    totalValue: 0
  });
  
  // State for UI control
  const [showSummary, setShowSummary] = useState(false);

  // Format currency with lakhs/crores
  const formatAmount = (value) => {
    value = Math.round(value);
    if (value >= 10000000) {
      return `₹${value.toLocaleString()} (${(value / 10000000).toFixed(2)} Crores)`;
    } else if (value >= 100000) {
      return `₹${value.toLocaleString()} (${(value / 100000).toFixed(2)} Lakhs)`;
    } else {
      return `₹${value.toLocaleString()} (${(value / 1000).toFixed(2)} Thousand)`;
    }
  };

  // Calculate SIP required for target amount
  const calculateTargetSIP = () => {
    const amount = parseFloat(targetAmount);
    const years = parseFloat(sipDuration);
    const rate = parseFloat(returnRate);

    // Validation
    if (isNaN(amount) || amount <= 0 || amount > 1000000000) {
      alert("Please enter a valid target amount (greater than 0 and up to ₹100,00,00,000).");
      return;
    }
    if (isNaN(years) || years <= 0 || years > 70) {
      alert("Please enter a valid duration (greater than 0 and up to 70 years).");
      return;
    }
    if (isNaN(rate) || rate <= 0 || rate > 40) {
      alert("Please enter a valid return rate (greater than 0 and up to 40%).");
      return;
    }

    // Calculate SIP required
    const months = years * 12;
    const monthlyRate = rate / 12 / 100;
    const futureValue = amount;
    
    // Correct SIP formula
    const sipRequired = futureValue * monthlyRate / 
      ((1 + monthlyRate) * (Math.pow(1 + monthlyRate, months) - 1));

    const investedAmount = sipRequired * months;
    const estimatedReturns = futureValue - investedAmount;

    // Update results
    setResults({
      requiredSip: sipRequired,
      investedAmount,
      estimatedReturns,
      totalValue: futureValue
    });
    
    setShowSummary(true);
  };

    // Handle moving to the next form
    const handleNext = () => {
      // Create a flat object matching the backend DTO
      const buyingCarPlan = {
        corpusRequired: results.totalValue,
        timePeriodYears: parseInt(sipDuration),
        expectedReturn: parseFloat(returnRate),
        sipRequired: results.requiredSip,
        totalInvestment: results.investedAmount,
        estimatedReturns: results.estimatedReturns
      };

      // Pass the structured data to the parent component
      onGoalComplete(buyingCarPlan);
    };

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
        Retirement Corpus 
        </h2>

      </div>
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Target Amount (₹)
            </label>
            <input
              type="number"
              value={targetAmount}
              onChange={(e) => setTargetAmount(e.target.value)}
              placeholder="e.g., 10,00,000"
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Investment Period (Years)
            </label>
            <input
              type="number"
              value={sipDuration}
              onChange={(e) => setSipDuration(e.target.value)}
              placeholder="e.g., 10"
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Expected Return (%)
            </label>
            <input
              type="number"
              value={returnRate}
              onChange={(e) => setReturnRate(e.target.value)}
              placeholder="e.g., 12"
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
      
      {showSummary && (
        <div className="mt-8">
          <h3 className="text-xl font-bold mb-4 text-gray-800 border-b pb-2">
            Summary
          </h3>
          
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-3 px-4 text-left font-semibold text-gray-700 border-b">Metric</th>
                  <th className="py-3 px-4 text-right font-semibold text-gray-700 border-b">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-gray-50">
                  <td className="py-3 px-4 text-gray-600 border-b">Required SIP (Monthly)</td>
                  <td className="py-3 px-4 text-right font-semibold border-b">
                    {formatAmount(results.requiredSip)}
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="py-3 px-4 text-gray-600 border-b">Invested Amount</td>
                  <td className="py-3 px-4 text-right font-semibold border-b">
                    {formatAmount(results.investedAmount)}
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="py-3 px-4 text-gray-600 border-b">Estimated Returns</td>
                  <td className="py-3 px-4 text-right font-semibold border-b">
                    {formatAmount(results.estimatedReturns)}
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="py-3 px-4 text-gray-600">Total Value</td>
                  <td className="py-3 px-4 text-right font-semibold">
                    {formatAmount(results.totalValue)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      <div className="flex flex-wrap gap-4 mt-8">
        <button
          onClick={calculateTargetSIP}
          className={`flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-md transition duration-300 ${showSummary ? 'hidden' : 'block'}`}
        >
          Calculate
        </button>
        
        <button
          onClick={handleNext}
          className={`flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-md transition duration-300 ${showSummary ? 'block' : 'hidden'}`}
          disabled={!showSummary}
        >
          Next
        </button>
        
        <button
          onClick={onBackToGoals}
          className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 px-4 rounded-md transition duration-300"
        >
          Back to Goals
        </button>
      </div>
    </div>
  );
};

export default RetirementCorpus;